import { useEffect, useState } from "react";
import client from "@/lib/api";
import { LEGENDTEAM_URL } from "@/lib/constants";
import { Check, Crown, Flag, Loader2, CreditCard, ExternalLink, Ticket } from "lucide-react";
import { toast } from "sonner";

const PLAN_META = {
  legend_base_yearly: {
    icon: Ticket,
    highlight: false,
    features: [
      "Accesso esclusivo a tutti gli articoli di legendmagazine.it",
      "Archivio digitale dei numeri mensili",
      "Newsletter con anteprime e retroscena",
    ],
  },
  legend_full_yearly: {
    icon: Crown,
    highlight: true,
    features: [
      "Tutto ciò che include l'abbonamento Base",
      "Tessera CONI per eventi Legend Team",
      "Accesso al Legend Village e agli eventi riservati",
      "Gadget e vantaggi dedicati agli abbonati Full",
    ],
  },
};

export default function Subscriptions() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    client.get("/payments/plans").then((r) => setPlans(r.data));
  }, []);

  const subscribe = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await client.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Impossibile avviare il pagamento. Riprova.");
      setLoading("");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="subscriptions-page">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-legend-red mb-3"><Crown size={14} /> Area Abbonati</span>
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl leading-none mb-4">Abbonati a Legend Magazine</h1>
        <p className="text-slate-600 dark:text-slate-300">Sostieni la redazione e accedi ai contenuti esclusivi del magazine di chi vive il motorsport con passione.</p>
      </div>

      <div className="max-w-md mx-auto mb-10">
        <label className="block text-sm font-semibold mb-1">La tua email (facoltativa, per la ricevuta)</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="subscription-email"
          placeholder="nome@email.it"
          className="w-full rounded-sm border border-slate-300 dark:border-slate-700 bg-white dark:bg-legend-paper px-4 py-3 focus:outline-none focus:border-legend-red"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {plans.map((p) => {
          const meta = PLAN_META[p.lookup_key];
          const Icon = meta.icon;
          return (
            <div
              key={p.lookup_key}
              data-testid={`plan-card-${p.lookup_key}`}
              className={`relative rounded-sm border-2 p-6 sm:p-8 bg-white dark:bg-legend-paper flex flex-col ${meta.highlight ? "border-legend-red shadow-xl" : "border-slate-200 dark:border-slate-800"}`}
            >
              {meta.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-legend-red text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm">Consigliato</span>
              )}
              <div className="flex items-center gap-3 mb-4">
                <span className={`grid place-items-center w-12 h-12 rounded-sm ${meta.highlight ? "bg-legend-red text-white" : "bg-legend-ink text-legend-yellow"}`}>
                  <Icon size={22} />
                </span>
                <h2 className="font-display font-black uppercase text-2xl">{p.name}</h2>
              </div>
              <div className="mb-6">
                <span className="font-display font-black text-5xl">{p.amount}€</span>
                <span className="text-slate-500 font-mono">/anno</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {meta.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <Check size={18} className="text-legend-red shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(p.lookup_key)}
                disabled={loading === p.lookup_key}
                data-testid={`subscribe-btn-${p.lookup_key}`}
                className={`w-full inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wide px-6 py-4 rounded-sm transition-colors disabled:opacity-60 ${meta.highlight ? "bg-legend-red hover:bg-legend-dark text-white" : "bg-legend-ink hover:bg-slate-800 text-white"}`}
              >
                {loading === p.lookup_key ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                Abbonati ora
              </button>
            </div>
          );
        })}
      </div>

      {/* Pilota / navigatore redirect */}
      <div className="max-w-4xl mx-auto mt-12 rounded-sm bg-legend-ink text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
        <div>
          <h3 className="font-display font-black uppercase text-2xl mb-1">Vuoi diventare pilota o navigatore?</h3>
          <p className="text-slate-300">Entra nel mondo Legend Team e corri con noi. Tutte le info sul sito ufficiale.</p>
        </div>
        <a href={LEGENDTEAM_URL} target="_blank" rel="noreferrer" data-testid="become-driver-btn" className="shrink-0 inline-flex items-center gap-2 bg-legend-yellow text-legend-ink font-bold uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-white transition-colors">
          <Flag size={16} /> Vai su legendteam.it <ExternalLink size={14} />
        </a>
      </div>

      <p className="text-center text-xs text-slate-400 font-mono mt-8">Pagamenti sicuri · Il rinnovo è annuale e può essere gestito in qualsiasi momento.</p>
    </div>
  );
}
